/* Приспособление к размеру окна: один порог на всё приложение и одна плотность
   таблиц на всё приложение.

   Порог здесь ровно один. Ноутбук 14" — это 1366×768, следом идёт 1440×900, и
   оба обязаны показывать работу, а не хром. Пороги, заведённые по месту, через
   полгода расходятся: на одном экране меню сворачивается на 1400, на соседнем —
   на 1280, и объяснить это никому нельзя. Поэтому значение одно, лежит здесь, и
   медиазапросы в CSS написаны по тому же числу.

   Порога по высоте нет: то, что съедает строки на 768, — это хром по бокам и
   панели в три ряда, а они меряются шириной. */
import { createContext, useContext, useLayoutEffect, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react'

/** Ширина окна, ниже которой интерфейс считается узким (1440×900 — узкий).
 *  То же число стоит в медиазапросе `styles/tokens/spacing.css` — правится вместе. */
export const NARROW_PX = 1500

const QUERY = `(max-width: ${NARROW_PX - 1}px)`

/* jsdom не реализует matchMedia вовсе, а не отвечает «не совпало»: без проверки
   любой тест, дошедший до этих строк, падает на «is not a function». */
const media = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : null

/** Узко ли окно прямо сейчас — для начальных значений состояния, до рендера. */
export function isNarrow(): boolean {
  return media()?.matches ?? false
}

function subscribe(onChange: () => void): () => void {
  const mq = media()
  if (!mq) return () => {}
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

/** Узко ли окно, с подпиской на изменение размера. */
export function useNarrow(): boolean {
  return useSyncExternalStore(subscribe, isNarrow, () => false)
}

/* Плотность таблиц — настройка рабочего места, как тема и язык: живёт в App,
   переживает перезагрузку и действует на ВСЕ таблицы сразу. Раньше это был
   локальный useState двух реестров: переключатель на реестре MDR не доходил ни
   до VDR, ни до трансмитталов, ни до журнала, а на экранах подрядчика его не
   было вовсе.

   Контекстом, а не свойством: таблицы стоят на восьми экранах, и протаскивать
   до каждой ещё одно свойство значит менять восемь подписей ради настройки,
   которая ни на одном из них не решается. */
interface DensityValue {
  /** Плотные строки (--row-h-compact вместо --row-h). */
  compact: boolean
  setCompact: (v: boolean) => void
}

const DensityCtx = createContext<DensityValue>({ compact: false, setCompact: () => {} })

export function DensityProvider({ value, children }: { value: DensityValue; children: ReactNode }) {
  return <DensityCtx.Provider value={value}>{children}</DensityCtx.Provider>
}

export function useDensity(): DensityValue {
  return useContext(DensityCtx)
}

/* Мастер-рельса — список слева от рабочей области: пакеты работ, очередь
   рассмотрения, реестры по дисциплинам, справочники. Все они стоят на одном и
   том же месте — первым элементом flex-строки внутри main, — поэтому доля
   считается от одной и той же ширины и правило может быть одно на всех.

   Фиксированная ширина отдавала рельсе тем большую долю, чем меньше экран: на
   1366 «Пакеты работ» вместе с меню занимали 604 px, то есть 44% экрана уходило
   хрому, а таблице оставалось 762. Теперь ширина — доля области, ограниченная
   снизу (уже список перестаёт читаться) и сверху (привычной шириной: шире расти
   незачем). Верхняя граница берётся на области около 1600 px. */
export function railWidth(max: number): string {
  return `clamp(${Math.round(max * 0.78)}px, ${(max / 16).toFixed(2)}%, ${max}px)`
}

/* Всплывающая панель раскрывается вниз от своего якоря: от кнопки в верхней
   полосе, от заголовка столбца. Высота у неё до сих пор была числом в стиле —
   360 у извещений, 220 у отбора по столбцу, — а число не знает, сколько под
   якорем осталось экрана. На 768 низ панели уходит за край окна: у приложения
   position: fixed на корне, прокрутки страницы нет вовсе, и нижние строки
   вместе с кнопками «Применить» становятся недостижимы.

   Поэтому высоту задаёт окно. Число в стиле остаётся верхней границей: панель
   не должна раскрываться во весь экран только потому, что место есть. */
/** Зазор до низа окна: панель впритык к краю читается как обрезанная. */
const DROP_GAP = 12
/** Ниже этого не сжимаем: панель в две строки бесполезна, пусть лучше вылезет. */
const DROP_MIN = 160

/** Сколько высоты остаётся панели от её верха до низа окна. */
export function useDropMaxHeight(ref: RefObject<HTMLElement | null>): number | undefined {
  const [max, setMax] = useState<number>()
  /* Слоем раскладки, а не эффектом: иначе панель успевает мигнуть полной
     высотой. Ограничение меняет высоту, но не верх панели, поэтому пересчёт
     сам себя не вызывает. */
  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current
      if (!el) return
      setMax(Math.max(DROP_MIN, window.innerHeight - el.getBoundingClientRect().top - DROP_GAP))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [ref])
  return max
}

/** Не пора ли раскрывать панель влево от якоря, а не вправо.
 *
 *  Меряется ЯКОРЬ (родитель панели — заголовок столбца), а не сама панель: у
 *  панели левый край зависит от того, перевернули её или нет, и измерение
 *  начало бы перезапускать само себя. У правых столбцов реестра панель уходила
 *  за край экрана на сотню пикселей — подписи значений обрезаны, «Сброс»
 *  наполовину, — а доскроллить до неё нельзя: она внутри той же прокручиваемой
 *  области, что и сам столбец. */
export function useDropFlip(ref: RefObject<HTMLElement | null>, width: number): boolean {
  const [flip, setFlip] = useState(false)
  useLayoutEffect(() => {
    const measure = () => {
      const anchor = ref.current?.parentElement
      if (!anchor) return
      setFlip(anchor.getBoundingClientRect().left + width > window.innerWidth - DROP_GAP)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [ref, width])
  return flip
}
